"use client";

import { create } from "zustand";
import { EnrichedAd } from "@sdk/shared/types/ads.types";
import { CreateQuickTradePayload, QuickTrade } from "@sdk/shared/types/order.types";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { ORDER_PAGE } from "@shared/src/config/page-links";
import { useNotificationStore } from "./useNotificationStore";

// Trade flow states
export type TradeFlowState = "form" | "searching" | "merchant_found" | "incoming_order" | "expired" | "p2p_flow";

// Animation states for smooth transitions
export type AnimationState = "idle" | "exiting" | "entering";

interface QuickTradeFlowStore {
  // Current state
  currentState: TradeFlowState;
  previousState: TradeFlowState | null;
  animationState: AnimationState;

  // Trade data
  merchantAdId: number | null;
  incomingOrder: QuickTrade | null;
  quickTradeId: string | null;
  acceptedOrderId: string | null;

  // Loading states
  isCreatingOrder: boolean;
  isCancelling: boolean;
  isSearching: boolean;

  // Authentication state
  showSignInModal: boolean;
  isAuthenticated: boolean;
  isSiweAuthenticating: boolean;

  // Error handling
  error: string | null;

  // State transition actions
  transitionToState: (newState: TradeFlowState, animationDuration?: number) => void;
  goToForm: () => void;
  goToSearching: (payload: CreateQuickTradePayload) => void;
  goToMerchantFound: (adId: number) => void;
  goToIncomingOrder: (order: QuickTrade) => void;
  goToExpired: () => void;
  goToP2PFlow: (orderHash: string) => void;

  // Data management actions
  setMerchantAdId: (adId: number | null) => void;
  setIncomingOrder: (order: QuickTrade | null) => void;
  setQuickTradeId: (id: string | null) => void;

  // Loading state actions
  setIsCreatingOrder: (loading: boolean) => void;
  setIsCancelling: (loading: boolean) => void;
  setIsSearching: (loading: boolean) => void;

  // Authentication actions
  setShowSignInModal: (show: boolean) => void;
  setIsAuthenticated: (authenticated: boolean) => void;
  setIsSiweAuthenticating: (authenticating: boolean) => void;

  // Error handling actions
  setError: (error: string | null) => void;
  clearError: () => void;

  // Animation actions
  setAnimationState: (state: AnimationState) => void;

  // Utility actions
  getStateDisplayName: () => string;

  // Reset actions
  resetTradeData: () => void;
  resetAll: () => void;
}

export const useQuickTradeFlowStore = create<QuickTradeFlowStore>((set, get) => ({
  // Initial state
  currentState: "form",
  previousState: null,
  animationState: "idle",

  // Initial trade data
  orderPayload: null,
  merchantAdId: null,
  incomingOrder: null,
  quickTradeId: null,
  acceptedOrderId: null,
  // Initial loading states
  isCreatingOrder: false,
  isCancelling: false,
  isSearching: false,

  // Initial authentication state
  showSignInModal: false,
  isAuthenticated: false,
  isSiweAuthenticating: false,

  // Initial error state
  error: null,

  // State transition actions
  transitionToState: (newState, animationDuration = 300) => {
    const { currentState } = get();

    // Don't transition if already in the target state
    if (currentState === newState) return;

    // Start exit animation
    set({ animationState: "exiting", previousState: currentState });

    // After animation delay, change state and start enter animation
    setTimeout(() => {
      set({
        currentState: newState,
        animationState: "entering",
        // Clear error when transitioning to new state
        error: null,
      });

      // Complete enter animation
      setTimeout(() => {
        set({ animationState: "idle" });
      }, animationDuration);
    }, animationDuration);
  },

  goToForm: () => {
    console.log("going to form");
    get().transitionToState("form");
    useNotificationStore.getState().setQuickTradeNotification(null);
    // Reset trade data when going back to form
    set({
      merchantAdId: null,
      incomingOrder: null,
      quickTradeId: null,
      isSearching: false,
    });
  },

  goToSearching: payload => {
    set({ isSearching: true });
    useNotificationStore.getState().setQuickTradeNotification({
      quickTrade: payload,
    });
    get().transitionToState("searching");
  },

  goToMerchantFound: adId => {
    set({ merchantAdId: adId, isSearching: false });
    get().transitionToState("merchant_found");
    console.log("went to merchant found");
  },

  goToIncomingOrder: order => {
    set({ incomingOrder: order });
    get().transitionToState("incoming_order");
  },

  goToExpired: () => {
    get().transitionToState("expired");
    set({
      merchantAdId: null,
      quickTradeId: null,
      isSearching: false,
    });
  },

  goToP2PFlow: (orderHash: string) => {
    const { context, router } = useHostStore.getState();
    if (context === "webApp") {
      // Ensure quick-trade page renders a valid view when user navigates back
      // by resetting the local flow state to "form" (and clearing transient data)
      // before leaving the page.
      router.push(ORDER_PAGE(orderHash));
      // timeout to prevent view from changing before navigation completes
      setTimeout(() => get().goToForm(), 2000);
    } else {
      set({ acceptedOrderId: orderHash });
      get().transitionToState("p2p_flow");
    }
  },

  // Data management actions
  setMerchantAdId: adId => set({ merchantAdId: adId }),
  setIncomingOrder: order => set({ incomingOrder: order }),
  setQuickTradeId: id => set({ quickTradeId: id }),

  // Loading state actions
  setIsCreatingOrder: loading => set({ isCreatingOrder: loading }),
  setIsCancelling: loading => set({ isCancelling: loading }),
  setIsSearching: loading => set({ isSearching: loading }),

  // Authentication actions
  setShowSignInModal: show => set({ showSignInModal: show }),
  setIsAuthenticated: authenticated => set({ isAuthenticated: authenticated }),
  setIsSiweAuthenticating: authenticating => set({ isSiweAuthenticating: authenticating }),

  // Error handling actions
  setError: error => set({ error }),
  clearError: () => set({ error: null }),

  // Animation actions
  setAnimationState: state => set({ animationState: state }),

  getStateDisplayName: () => {
    const { currentState } = get();
    switch (currentState) {
      case "form":
        return "Trade Form";
      case "searching":
        return "Finding Merchant";
      case "merchant_found":
        return "Merchant Found";
      case "incoming_order":
        return "Incoming Order";
      case "expired":
        return "Expired";
      case "p2p_flow":
        return "Order Process";
      default:
        return "Unknown State";
    }
  },

  // Reset actions
  resetTradeData: () =>
    set({
      merchantAdId: null,
      incomingOrder: null,
      quickTradeId: null,
      isSearching: false,
      error: null,
    }),

  resetAll: () => {
    get().resetTradeData();
    set({
      currentState: "form",
      previousState: null,
      animationState: "idle",
      isCreatingOrder: false,
      isCancelling: false,
      showSignInModal: false,
    });
  },
}));
